import { Button, Input, Switch } from '@insly/qmt-reactjs-ui-lib';
import React, { useState } from 'react';
import { AxiosError } from 'axios';
import { TFetchStatus } from 'models';
import { errorNotification } from 'utils/errorNotification';
import { useApp } from 'context/App';
import LoadingOverlay from 'helpers/LoadingOverlay';
import {
  GeneralSettingsStyled,
  GeneralSettingsStyledControls,
  GeneralSettingsStyledHeader,
  GeneralSettingsStyledHeaderTitle,
  GeneralSettingsStyledSection,
  GeneralSettingsStyledSectionHeader,
} from './styles';
import { IBrokerProps } from 'app/Pages/common';
import { cloneObject, translate } from 'utils';
import { DEFAULT_CEPIK_INSURERS } from '../CepikSettings/constants';

interface IGeneralSettings {
  onSubmit: (props: IBrokerProps, onError: (errors: AxiosError) => void, onSuccess: () => void) => void,
  props?: IBrokerProps,
}

const GeneralSettings = ({
  onSubmit,
  props
}: IGeneralSettings) => {
  const { showNotification } = useApp();
  const [form, setForm] = useState(cloneObject(props || {}));

  const [fetchStatus, setFetchStatus] = useState<TFetchStatus>(null);

  const onError = (error: AxiosError) => {
    setFetchStatus('failed');
    errorNotification(error, showNotification);
  };

  const onSuccess = () => setFetchStatus('success');

  const handleChange = (name: string, value?: boolean | string | Record<string, any>) => {

    if (name === 'insurer_gtc_enabled' && !value) {
      setForm({
        ...form,
        [name]: value,
        customer_email_offers_enabled: false
      });

      return;
    }

    setForm({
      ...form,
      [name]: value
    });
  };

  const handleSubmit = () => {
    setFetchStatus('loading');
    const propsTouched = form ? cloneObject(form) : {};
    const cepikJustEnabled = !props?.cepik?.enabled && propsTouched?.cepik?.enabled;
    const insurersUnset = !propsTouched?.cepik?.insurers || !propsTouched.cepik.insurers.length;
    if (cepikJustEnabled && insurersUnset) {
      propsTouched.cepik = {
        ...propsTouched.cepik,
        insurers: [...DEFAULT_CEPIK_INSURERS],
      };
    }
    onSubmit(propsTouched, onError, onSuccess);
  };

  return (
    <>
      {fetchStatus === 'loading' ? <LoadingOverlay /> : null}
      <GeneralSettingsStyled>

        {props?.idd_required ? (
          <IddRequired
            iddRequired={form?.idd_required}
            handleChange={(value: IBrokerProps['idd_required']) => handleChange('idd_required', value)}
          />
        ) : null}

        <GeneralSettingsStyledHeader>
          <Switch
            name="sync"
            handleChange={(name, value) => handleChange(name as string, value as boolean)}
            checked={form?.sync}
          />
          <GeneralSettingsStyledHeaderTitle>
            {translate({ key: 'master.broker.settings.sync' })}
          </GeneralSettingsStyledHeaderTitle>
        </GeneralSettingsStyledHeader>
        <GeneralSettingsStyledHeader>
          <Switch
            name="gdpr_enabled"
            handleChange={(name, value) => handleChange(name as string, value as boolean)}
            checked={form?.gdpr_enabled}
          />
          <GeneralSettingsStyledHeaderTitle>
            {translate({ key: 'master.broker.settings.gdpr' })}
          </GeneralSettingsStyledHeaderTitle>
        </GeneralSettingsStyledHeader>
        <GeneralSettingsStyledHeader>
          <Switch
            name="idd_email_edit_content_preview"
            handleChange={(name, value) => handleChange(name as string, value as boolean)}
            checked={form?.idd_email_edit_content_preview}
          />
          <GeneralSettingsStyledHeaderTitle>
            {translate({ key: 'master.broker.settings.idd_email_preview_edit' })}
          </GeneralSettingsStyledHeaderTitle>
        </GeneralSettingsStyledHeader>
        <GeneralSettingsStyledHeader>
          <Switch
            name="gdpr_email_edit_content_preview"
            handleChange={(name, value) => handleChange(name as string, value as boolean)}
            checked={form?.gdpr_email_edit_content_preview}
          />
          <GeneralSettingsStyledHeaderTitle>
            {translate({ key: 'master.broker.settings.gdpr_email_preview_edit' })}
          </GeneralSettingsStyledHeaderTitle>
        </GeneralSettingsStyledHeader>
        <GeneralSettingsStyledHeader>
          <Switch
            name="hide_external_issuing_link"
            handleChange={(name, value) => handleChange(name as string, value as boolean)}
            checked={form?.hide_external_issuing_link}
          />
          <GeneralSettingsStyledHeaderTitle>
            {translate({ key: 'master.broker.settings.hide_external_issuing_link' })}
          </GeneralSettingsStyledHeaderTitle>
        </GeneralSettingsStyledHeader>
        <GeneralSettingsStyledHeader>
          <Switch
            name="online_quote_notification_email_enabled"
            handleChange={(name, value) => handleChange(name as string, value as boolean)}
            checked={form?.online_quote_notification_email_enabled}
          />
          <GeneralSettingsStyledHeaderTitle>
            {translate({ key: 'master.broker.settings.online_quote_notification_email_enabled' })}
          </GeneralSettingsStyledHeaderTitle>
        </GeneralSettingsStyledHeader>
        <GeneralSettingsStyledHeader>
          <Input
            name="online_quote_notification_email"
            handleChange={(name, value) => handleChange(name as string, value as string)}
            value={form?.online_quote_notification_email}
            disabled={!form?.online_quote_notification_email_enabled}
          />
          <GeneralSettingsStyledHeaderTitle>
            {translate({ key: 'master.broker.settings.online_quote_notification_email' })}
          </GeneralSettingsStyledHeaderTitle>
        </GeneralSettingsStyledHeader>
        <GeneralSettingsStyledHeader>
          <Switch
            name="sms_enabled"
            handleChange={(name, value) => handleChange(name as string, value as boolean)}
            checked={form?.sms_enabled}
          />
          <GeneralSettingsStyledHeaderTitle>
            {translate({ key: 'master.broker.settings.sms_enabled' })}
          </GeneralSettingsStyledHeaderTitle>
        </GeneralSettingsStyledHeader>
        <GeneralSettingsStyledHeader>
          <Switch
            name="sms_enabled_gdpr"
            handleChange={(name, value) => handleChange(name as string, value as boolean)}
            checked={form?.sms_enabled_gdpr}
          />
          <GeneralSettingsStyledHeaderTitle>
            {translate({ key: 'master.broker.settings.sms_enabled_gdpr' })}
          </GeneralSettingsStyledHeaderTitle>
        </GeneralSettingsStyledHeader>
        <GeneralSettingsStyledHeader>
          <Input
            name="sms_access_token"
            handleChange={(name, value) => handleChange(name as string, value as string)}
            value={form?.sms_access_token}
            disabled={!form?.sms_enabled}
          />
          <GeneralSettingsStyledHeaderTitle>
            {translate({ key: 'master.broker.settings.sms_access_token' })}
          </GeneralSettingsStyledHeaderTitle>
        </GeneralSettingsStyledHeader>
        <GeneralSettingsStyledHeader>
          <Switch
            name="insurer_gtc_enabled"
            handleChange={(name, value) => handleChange(name as string, value as boolean)}
            checked={form?.insurer_gtc_enabled}
          />
          <GeneralSettingsStyledHeaderTitle>
            {translate({ key: 'master.broker.settings.insurer_gtc_enabled' })}
          </GeneralSettingsStyledHeaderTitle>
        </GeneralSettingsStyledHeader>

        <GeneralSettingsStyledHeader>
          <Switch
            name="customer_email_offers_enabled"
            handleChange={(name, value) => handleChange(name as string, value as boolean)}
            checked={form?.customer_email_offers_enabled}
            disabled={!form?.insurer_gtc_enabled}
          />
          <GeneralSettingsStyledHeaderTitle>
            {translate({ key: 'master.broker.settings.customer_email_offers_enabled' })}
          </GeneralSettingsStyledHeaderTitle>
        </GeneralSettingsStyledHeader>

        <GeneralSettingsStyledHeader>
          <Switch
            name="cepik_enabled"
            handleChange={(_name, value) => handleChange('cepik', {
              ...form?.cepik,
              enabled: value as boolean,
            })}
            checked={form?.cepik?.enabled}
          />
          <GeneralSettingsStyledHeaderTitle>
            {translate({ key: 'master.broker.settings.cepik_enabled' })}
          </GeneralSettingsStyledHeaderTitle>
        </GeneralSettingsStyledHeader>

        <GeneralSettingsStyledHeader>
          <Switch
            name="forward_to_policy_issuance_enabled"
            handleChange={(name, value) => handleChange(name as string, value as boolean)}
            checked={form?.forward_to_policy_issuance_enabled}
          />
          <GeneralSettingsStyledHeaderTitle>
            {translate({ key: 'master.broker.settings.forward_to_policy_issuance_enabled' })}
          </GeneralSettingsStyledHeaderTitle>
        </GeneralSettingsStyledHeader>
        <GeneralSettingsStyledHeader>
          <Input
            name="forward_to_policy_issuance_email"
            handleChange={(name, value) => handleChange(name as string, value as string)}
            value={form?.forward_to_policy_issuance_email}
            disabled={!form?.forward_to_policy_issuance_enabled}
          />
          <GeneralSettingsStyledHeaderTitle>
            {translate({ key: 'master.broker.settings.forward_to_policy_issuance_email' })}
          </GeneralSettingsStyledHeaderTitle>
        </GeneralSettingsStyledHeader>

        <GeneralSettingsStyledControls>
          <Button size="l" onClick={handleSubmit}>{translate({ key: 'common.button.save' })}</Button>
        </GeneralSettingsStyledControls>
      </GeneralSettingsStyled>
    </>
  );
};

const IddRequired = ({
  iddRequired,
  handleChange
} : {
  iddRequired: IBrokerProps['idd_required'],
  handleChange: (value: IBrokerProps['idd_required']) => void,
}) => {

  if (iddRequired) {
    const items = Object.keys(iddRequired).map(productKey => (
      <GeneralSettingsStyledHeader>
        <Switch
          name={productKey}
          handleChange={(name, value) => handleChange({
            ...iddRequired,
            [productKey]: value as boolean
          })}
          checked={iddRequired[productKey]}
        />
        <GeneralSettingsStyledHeaderTitle>
          {translate({ key: `common.quote.type.${productKey}` })}
        </GeneralSettingsStyledHeaderTitle>
      </GeneralSettingsStyledHeader>
    ));

    return (
      <GeneralSettingsStyledSection>
        <GeneralSettingsStyledSectionHeader>
          {translate({ key: `master.broker.settings.idd_required` })}
        </GeneralSettingsStyledSectionHeader>
        {items}
      </GeneralSettingsStyledSection>
    );
  }

  return <div />;

};

export default GeneralSettings;
